/**
 * OrgSwitcher — header widget that shows the active org's name and,
 * when the user has memberships in multiple orgs, opens a dropdown
 * to switch between them.
 *
 * Single-org users see the org name as static text (no chevron, no
 * interactivity) — the visual matches the previous static
 * `{brand.name}` rendering so existing single-org tenants are
 * unaffected.
 *
 * Multi-org users (Cicely, Maria) see a button with a chevron;
 * clicking opens a dropdown of all orgs they're a member of, with the
 * current one checked. Selecting another org calls switch-organization
 * + refreshes the session + reloads the page so the new tenant's data
 * is rendered cleanly under the new JWT.
 *
 * Visible variant ("compact") for the mobile header, "full" for the
 * sidebar.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation }                               from 'react-i18next'
import { useUserOrgMemberships }                        from '@/hooks/useUserOrgMemberships'
import { useSwitchOrganization }                        from '@/hooks/useSwitchOrganization'
import { useAuthStore }                                 from '@/store/authStore'
import { isAppError }                                   from '@/lib/errors/AppError'

export interface OrgSwitcherProps {
  /** "compact" for mobile header, "full" for sidebar. */
  variant?:    'compact' | 'full'
  /** Display text when the active org name can't be resolved. */
  fallback?:   string
}

export function OrgSwitcher({ variant = 'full', fallback = '' }: OrgSwitcherProps): ReactNode {
  const { t }      = useTranslation()
  const memberships = useUserOrgMemberships()
  const switchOrg   = useSwitchOrganization()
  const sessionOrgId = useAuthStore((s) => {
    const meta = s.user?.app_metadata as { org_id?: string } | undefined
    return meta?.org_id ?? null
  })

  const [open, setOpen] = useState(false)
  const containerRef    = useRef<HTMLDivElement | null>(null)

  // Close on outside click.
  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) return
      if (!containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const rows = memberships.data ?? []
  const activeRow =
    rows.find((r) => r.organizationId === sessionOrgId)
    ?? rows.find((r) => r.isDefault)
    ?? rows[0]
  const activeName = activeRow?.organizationName ?? fallback
  const isMulti    = rows.length > 1
  const errMsg     = switchOrg.error && isAppError(switchOrg.error) ? switchOrg.error.userMessage : null

  function handlePick(orgId: string): void {
    setOpen(false)
    if (orgId === sessionOrgId) return
    void switchOrg.mutateAsync({ targetOrganizationId: orgId })
  }

  // Single-org users: render plain text matching the prior static
  // `{brand.name}` look. No chevron, no dropdown wiring.
  if (!isMulti) {
    return (
      <strong
        data-testid="org-switcher-single"
        style={{
          fontFamily: 'var(--brand-font-family-display)',
          fontSize:   variant === 'compact' ? '0.9rem' : '0.95rem',
          lineHeight: 1.15,
        }}
      >
        {activeName}
      </strong>
    )
  }

  // Multi-org users: button + dropdown.
  return (
    <div
      ref={containerRef}
      data-testid="org-switcher"
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="org-switcher-toggle"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={switchOrg.isPending}
        style={{
          background:    'transparent',
          border:        '1px solid var(--brand-neutral-300)',
          borderRadius:  'var(--brand-radius-sm)',
          padding:       variant === 'compact' ? '0.25rem 0.5rem' : '0.35rem 0.6rem',
          fontFamily:    'var(--brand-font-family-display)',
          fontSize:      variant === 'compact' ? '0.85rem' : '0.95rem',
          color:         'var(--brand-neutral-900)',
          cursor:        switchOrg.isPending ? 'wait' : 'pointer',
          display:       'inline-flex',
          alignItems:    'center',
          gap:           '0.4rem',
          maxWidth:      variant === 'compact' ? '11rem' : '17rem',
        }}
      >
        <span
          data-testid="org-switcher-active-name"
          style={{
            overflow:     'hidden',
            textOverflow: 'ellipsis',
            whiteSpace:   'nowrap',
          }}
        >
          {switchOrg.isPending ? t('orgSwitcher.switching', 'Switching…') : activeName}
        </span>
        <span aria-hidden="true" style={{ fontSize: '0.7rem', color: 'var(--brand-neutral-500)' }}>▾</span>
      </button>

      {open && (
        <ul
          role="listbox"
          data-testid="org-switcher-list"
          style={{
            position:        'absolute',
            top:             'calc(100% + 0.25rem)',
            left:            0,
            zIndex:          50,
            minWidth:        '14rem',
            margin:          0,
            padding:         '0.3rem 0',
            background:      'var(--brand-white)',
            border:          '1px solid var(--brand-neutral-300)',
            borderRadius:    'var(--brand-radius-sm)',
            boxShadow:       '0 6px 18px rgba(0,0,0,0.08)',
            listStyle:       'none',
          }}
        >
          {rows.map((r) => {
            const selected = r.organizationId === sessionOrgId
            return (
              <li
                key={r.id}
                role="option"
                aria-selected={selected}
                data-testid={`org-switcher-option-${r.organizationId}`}
                onClick={() => handlePick(r.organizationId)}
                style={{
                  padding:       '0.45rem 0.75rem',
                  cursor:        'pointer',
                  fontSize:      '0.9rem',
                  color:         'var(--brand-neutral-900)',
                  background:    selected ? 'var(--brand-primary-subtle)' : 'transparent',
                  display:       'flex',
                  alignItems:    'center',
                  justifyContent:'space-between',
                  gap:           '0.5rem',
                }}
              >
                <span style={{ fontWeight: selected ? 600 : 400 }}>{r.organizationName}</span>
                {selected && (
                  <span aria-hidden="true" style={{ color: 'var(--brand-primary)', fontSize: '0.85rem' }}>✓</span>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {errMsg && (
        <p
          role="alert"
          data-testid="org-switcher-error"
          style={{
            position:   'absolute',
            top:        'calc(100% + 0.25rem)',
            left:       0,
            margin:     0,
            padding:    '0.25rem 0.5rem',
            background: 'var(--brand-danger)',
            color:      'var(--brand-white)',
            fontSize:   '0.75rem',
            borderRadius:'var(--brand-radius-sm)',
            whiteSpace: 'nowrap',
          }}
        >
          {errMsg}
        </p>
      )}
    </div>
  )
}
